import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LoadingState, EmptyState } from "@/components/shared";
import { Initials, scoreTone } from "@/components/job/Lifecycle";
import Funnel from "@/pages/recruiter/Funnel";
import { Users } from "lucide-react";

const STAGE_LABEL = { applied: "Applied", screening: "Screening", interview: "Interview", offer: "Offer", hired: "Hired", rejected: "Not progressing" };

function Kpi({ value, label, testId }) {
  return (
    <div className="rounded-xl border border-border bg-card px-5 py-4" data-testid={testId}>
      <div className="text-[28px] font-display font-bold leading-tight">{value}</div>
      <div className="text-[13px] text-muted-foreground">{label}</div>
    </div>
  );
}

export function TopCandidates({ cands, jobId, title = "Top candidates" }) {
  const nav = useNavigate();
  return (
    <section className="rounded-xl border border-border bg-card" data-testid="top-candidates">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border/70">
        <h2 className="font-semibold">{title}</h2>
        <button onClick={() => nav(`/recruiter/pipeline?job=${jobId}`)} className="text-sm text-primary hover:underline" data-testid="open-board">Open board →</button>
      </div>
      <div className="divide-y divide-border/60">
        {cands.map((c) => (
          <button key={c.id} onClick={() => nav(`/recruiter/candidate/${c.id}`)} className="w-full text-left px-5 py-3.5 grid grid-cols-[auto_minmax(0,1fr)_auto_auto] sm:grid-cols-[auto_minmax(0,1fr)_120px_80px_48px] items-center gap-3 hover:bg-foreground/[0.03] transition-colors" data-testid={`top-candidate-${c.id}`}>
            <Initials name={c.name} />
            <span className="min-w-0"><span className="block text-sm font-semibold truncate">{c.name}</span><span className="block text-xs text-muted-foreground truncate">{c.headline || c.parsed_profile?.headline || c.email}</span></span>
            <span className="hidden sm:block text-sm text-muted-foreground">{STAGE_LABEL[c.stage] || c.stage}</span>
            <span className="text-xs text-muted-foreground">{c.objective_score != null ? `Test ${c.objective_score}` : c.assessment?.status === "completed" ? "Assessed" : "Not assessed"}</span>
            <span className={`text-right font-bold ${scoreTone(c.overall_score)}`}>{c.overall_score ?? "—"}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

export function ScreeningStep({ job }) {
  const [cands, setCands] = useState(null);
  useEffect(() => { api.get(`/jobs/${job.id}/candidates`).then((r) => setCands(r.data)).catch(() => setCands([])); }, [job.id]);

  const stats = useMemo(() => {
    if (!cands) return null;
    const active = cands.filter((c) => c.stage !== "rejected");
    const assessed = cands.filter((c) => c.assessment?.status === "completed").length;
    const scores = cands.map((c) => c.overall_score).filter((n) => n != null).sort((a, b) => a - b);
    const day0 = new Date(); day0.setHours(0, 0, 0, 0);
    const series = Array.from({ length: 14 }, (_, i) => {
      const start = day0.getTime() - (13 - i) * 864e5;
      return cands.filter((c) => { const t = Date.parse(c.applied_at || ""); return t >= start && t < start + 864e5; }).length;
    });
    return {
      total: cands.length, active, assessed, median: scores.length ? scores[Math.floor(scores.length / 2)] : null,
      interview: cands.filter((c) => c.stage === "interview").length, series,
      top: [...active].sort((a, b) => (b.overall_score ?? -1) - (a.overall_score ?? -1)).slice(0, 5),
      byStage: Object.keys(STAGE_LABEL).map((k) => [k, cands.filter((c) => c.stage === k).length]),
    };
  }, [cands]);

  if (!stats) return <LoadingState label="Loading applicants…" />;
  if (job.status !== "published" && stats.total === 0) return <div className="app-container py-8"><EmptyState icon={Users} title="Screening starts once the role is live" description="Publish the role and applicants will appear here, ranked by explainable fit." /></div>;
  if (stats.total === 0) return <div className="app-container py-8"><EmptyState icon={Users} title="No applicants yet" description="Share the public link with Copy link at the top. Applicants appear here as soon as they apply and complete the assessment." /></div>;
  const max = Math.max(...stats.series, 1);

  return (
    <div className="app-container py-8 space-y-6" data-testid="screening-step">
      <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
        <div className="space-y-6 min-w-0">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            <Kpi value={stats.total} label="Applicants" testId="kpi-applicants" />
            <Kpi value={stats.assessed} label={`Assessed · ${Math.round((stats.assessed / stats.total) * 100)}%`} testId="kpi-assessed" />
            <Kpi value={stats.median ?? "—"} label="Median fit" testId="kpi-median" />
            <Kpi value={stats.interview} label="In interview" testId="kpi-interview" />
          </div>
          <TopCandidates cands={stats.top} jobId={job.id} />
        </div>
        <div className="space-y-4">
          <section className="rounded-xl border border-border bg-card p-5" data-testid="applications-chart">
            <h2 className="font-semibold mb-4">Applications · 14 days</h2>
            <div className="flex items-end gap-1.5 h-20" aria-hidden="true">
              {stats.series.map((v, i) => <span key={i} className={`flex-1 rounded-sm ${v === max && v > 0 ? "bg-primary" : "bg-primary/30"}`} style={{ height: `${Math.max(6, (v / max) * 100)}%` }} />)}
            </div>
            <p className="text-xs text-muted-foreground mt-3">{stats.series.reduce((a, b) => a + b, 0)} applied in the last two weeks</p>
          </section>
          <section className="rounded-xl border border-border bg-card p-5" data-testid="stage-breakdown">
            <h2 className="font-semibold mb-3">By stage</h2>
            <dl className="space-y-2 text-sm">
              {stats.byStage.map(([k, n]) => <div key={k} className="flex justify-between"><dt className="text-muted-foreground">{STAGE_LABEL[k]}</dt><dd className="font-mono">{n}</dd></div>)}
            </dl>
          </section>
        </div>
      </div>
      <Funnel inWorkspace />
    </div>
  );
}
